/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';
import { setLocation } from 'js/lib/coursera.redirect';
import keysToConstants from 'js/lib/keysToConstants';

import { Button, Dialog, Divider, Link, Typography2 } from '@coursera/cds-core';
import type { ButtonProps } from '@coursera/cds-core';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
import {
  generateImpressionForPrePurchaseUpsellExperiment,
  previewPrePurchaseUpsellVariant,
} from 'bundles/coursera-plus-common/utils/prePurchaseUpsellExperimentUtils';
import type { EnrollmentChoiceTypesValues } from 'bundles/enroll-common/common/EnrollmentChoiceTypes';
import {
  AUDIT_COURSE,
  ENROLL_COURSE,
  PURCHASE_SINGLE_COURSE,
  SUBSCRIBE_TO_COURSERA_PLUS,
} from 'bundles/enroll-common/common/EnrollmentChoiceTypes';
import {
  canPurchaseSingleCourseAndHasFullDiscount,
  enrollWithFullDiscountPromotion,
} from 'bundles/enroll-common/lib/apiClient';
import {
  choiceTypeToHandleSubmitPromise,
  submitEnrollmentPromise,
} from 'bundles/enroll-common/lib/enrollmentChoiceUtils';
import type { ApiError } from 'bundles/enroll-common/utils/errorUtils';
import {
  canEnrollThroughFreemium,
  isFreemiumModelEnabled,
  useFreemiumTracking,
} from 'bundles/enroll-common/utils/freemiumModelUtils';
import EnrollErrorModal from 'bundles/enroll-modal/components/common/EnrollErrorModal';
import PromotionApplicableCheckoutMessage from 'bundles/enroll-modal/components/common/PromotionApplicableCheckoutMessage';
import CourseraPlusPrePurchaseUpsellModal from 'bundles/enroll-modal/components/coursera-plus/prePurchaseUpsell/CourseraPlusPrePurchaseUpsellModal';
import EnrollmentReasonMessageV2 from 'bundles/enroll-modal/components/standalone-course/EnrollmentReasonMessageV2';
import {
  generateStandaloneCourseModalCopyImpression,
  getStandaloneCourseEnrollModalCopy,
} from 'bundles/enroll-modal/components/standalone-course/utils/standaloneCourseModalCopy';
import { useCourseEnrollModalData } from 'bundles/enroll-modal/data/usePageData';
import { useEnrollModalEventing } from 'bundles/enroll-modal/utils/eventingUtils';
import PriceWithDiscountIndicator from 'bundles/ownable-products-common/components/PriceWithDiscountIndicator';
import type { ProductPrices } from 'bundles/ownable-products-common/hooks/useGetProductPrices';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import { certDpURLForCertificatePilotCourse } from 'bundles/premium-certificate/utils/certificatePilotUtils';

import _t from 'i18n!nls/enroll-course';

const MODAL_TYPES = keysToConstants(['ENROLL', 'ERROR', 'PRE_PURCHASE_UPSELL']);

const TrackedButton = withSingleTracked({ type: 'BUTTON' })<ButtonProps>(Button);

type PropsFromCaller = {
  onClose: () => void;
  s12nId?: string;
  isPrePurchaseUpsellEligible?: boolean;
};

const styles = {
  auditCtaContainer: css`
    margin-top: 32px;
  `,
  auditCta: css`
    padding-top: 31px;
  `,
  promoMessage: css`
    padding-bottom: var(--cds-spacing-100);
  `,
};

// For Freemium model flow
export const StandaloneCourseEnrollModalV3 = ({ s12nId, onClose, isPrePurchaseUpsellEligible }: PropsFromCaller) => {
  const {
    course,
    s12n,
    enrollmentAvailableChoices,
    promotionEligibilities,
    promotionDetails,
    productPrice,
    courseTypeMetadataWithVersion,
  } = useCourseEnrollModalData();

  const [didClickContinue, setDidClickContinue] = useState<boolean>(false);
  const [didClickPreview, setDidClickPreview] = useState<boolean>(false);
  const [activeModal, setActiveModal] = useState<keyof typeof MODAL_TYPES>(MODAL_TYPES.ENROLL);
  const [error, setError] = useState<ApiError | undefined>();

  const { getEnrollModalTrackingRef, trackEnrollModalContinue, trackEnrollModalClose } = useEnrollModalEventing({
    isUsingPrePurchaseUpsellFlow: activeModal === MODAL_TYPES.PRE_PURCHASE_UPSELL,
  });

  const visibilityTrackingRef = getEnrollModalTrackingRef();

  const isPrePurchaseUpsellEnabled =
    isPrePurchaseUpsellEligible && ['1', '2'].includes(previewPrePurchaseUpsellVariant());

  const isEnrollingThroughFreemium = canEnrollThroughFreemium({
    courseId: course.id,
    enrollmentAvailableChoices,
    courseTypeMetadataWithVersion,
  });

  // Impress the user in the standalone course modal copy experiment when the modal is rendered
  const copyVariant = generateStandaloneCourseModalCopyImpression();
  const { trackPreviewLinkClick, trackPreviewLinkView } = useFreemiumTracking();

  useEffect(() => {
    if (isEnrollingThroughFreemium) {
      trackPreviewLinkView();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnrollingThroughFreemium]);

  const { HeadingGroup, Content, Actions } = Dialog;

  const getChoiceTypes = useCallback((): Array<EnrollmentChoiceTypesValues> => {
    const choiceTypes = enrollmentAvailableChoices.choiceTypes ?? [];

    // Don't show the Coursera Plus option for standalone course enroll modal
    return choiceTypes
      .filter((choiceType: EnrollmentChoiceTypesValues) => choiceType !== SUBSCRIBE_TO_COURSERA_PLUS)
      ?.sort((choiceType) => {
        // Enforce that the audit / free enroll option always appears last in the modal
        if (choiceType === AUDIT_COURSE || choiceType === ENROLL_COURSE) {
          return 1;
        }
        return -1;
      });
  }, [enrollmentAvailableChoices.choiceTypes]);

  let reasonCode = enrollmentAvailableChoices.enrollmentChoiceReasonCode;
  if (enrollmentAvailableChoices.isEnrolled && enrollmentAvailableChoices.hasChoice) {
    reasonCode = undefined;
  }

  const handleModalClose = () => {
    trackEnrollModalClose();
    onClose();
  };

  const redirectToCourseHome = () => {
    const courseSlug = course?.slug;

    if (courseSlug) {
      return setLocation(`/learn/${courseSlug}/home/welcome`);
    }
    return setLocation('/');
  };

  const handleError = (data: ApiError) => {
    setDidClickContinue(false);
    setActiveModal(MODAL_TYPES.ERROR);
    setError(data);
  };

  const proceedToCheckout = () => {
    setDidClickContinue(true);

    if (certDpURLForCertificatePilotCourse(course.id)) {
      window.open(certDpURLForCertificatePilotCourse(course.id), '_blank');
      setDidClickContinue(false);
      return;
    }

    let selectedChoiceType = null;
    const choiceTypes = enrollmentAvailableChoices.choiceTypes;

    if (choiceTypes?.length >= 1) {
      selectedChoiceType = choiceTypes[0];
    } else {
      throw new Error('Cannot continue enrollment without selected choice type');
    }

    // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    trackEnrollModalContinue(selectedChoiceType);

    const promoCode = promotionEligibilities?.isEligible ? promotionEligibilities.promoCodeId : undefined;

    const options = {
      courseId: course.id,
    };

    // @ts-expect-error ts-migrate(7015) FIXME: Element implicitly has an 'any' type because index... Remove this comment to see the full error message
    const handleSubmitPromise = choiceTypeToHandleSubmitPromise[selectedChoiceType];
    submitEnrollmentPromise({
      handleSubmitPromise,
      options,
      promoCode,
    })
      .then((data: $TSFixMe /* TODO: type submitEnrollmentPromise */) => {
        if (data.didEnroll) {
          redirectToCourseHome();
        }
      })
      .catch(handleError);
  };

  const onClickContinue = () => {
    if (isPrePurchaseUpsellEligible) {
      generateImpressionForPrePurchaseUpsellExperiment();
    }

    if (isPrePurchaseUpsellEnabled) {
      setActiveModal(MODAL_TYPES.PRE_PURCHASE_UPSELL);
      return;
    }

    proceedToCheckout();
  };

  const handlePrePurchaseUpsellClose = (shouldGoToCheckoutOnClose: boolean) => {
    if (shouldGoToCheckoutOnClose) {
      proceedToCheckout();
    } else {
      handleModalClose();
    }
  };

  const onClickAuditCourse = (e: React.SyntheticEvent<HTMLButtonElement>) => {
    e.preventDefault();

    setDidClickPreview(true);

    const choiceTypes = getChoiceTypes();

    if (isEnrollingThroughFreemium) {
      trackPreviewLinkClick();
    }

    const enrollmentChoiceType = choiceTypes[choiceTypes.length - 1] as string;
    trackEnrollModalContinue(enrollmentChoiceType);

    // @ts-expect-error ts-migrate(7015) FIXME: Element implicitly has an 'any' type because index... Remove this comment to see the full error message
    const handleSubmitPromise = choiceTypeToHandleSubmitPromise[enrollmentChoiceType];
    const options = {
      courseId: course.id,
      isFreemiumLearner: isEnrollingThroughFreemium,
    };

    return submitEnrollmentPromise({ handleSubmitPromise, options }).then(redirectToCourseHome);
  };

  const checkoutAndGoToCourseWithFullDiscount = () => {
    setDidClickContinue(true);

    enrollWithFullDiscountPromotion({
      promotionEligibilities,
    })
      .then(() => {
        setLocation(course.homeLink);
      })
      .catch((data: $TSFixMe /* TODO: type submitEnrollmentPromise */) => {
        setDidClickContinue(false);
        setActiveModal(MODAL_TYPES.ERROR);
        setError(data);
      });
  };

  const renderProductPrice = (descriptionProductPrice?: ProductPrices) => {
    if (!descriptionProductPrice) {
      return null;
    }

    if (
      canPurchaseSingleCourseAndHasFullDiscount({
        enrollmentAvailableChoices,
        promotionDetails,
        promotionEligibilities,
      })
    ) {
      return (
        <PriceWithDiscountIndicator
          amount={descriptionProductPrice.amount}
          finalAmount={0}
          currencyCode={descriptionProductPrice.currencyCode}
          discountColorType="green"
          hideCurrencyCode={true}
          showDiscountPrice={true}
          showDiscountPercent={false}
        />
      );
    } else {
      return (
        <PriceWithDiscountIndicator
          amount={descriptionProductPrice.amount}
          finalAmount={descriptionProductPrice.finalAmount}
          currencyCode={descriptionProductPrice.currencyCode}
          hideCurrencyCode={true}
        />
      );
    }
  };

  const { headerMessage, bodyMessage, primaryCta, secondaryCta } = getStandaloneCourseEnrollModalCopy({
    variant: copyVariant,
    courseName: course.name,
    price: renderProductPrice(productPrice),
    secondaryCtaAction: onClickAuditCourse,
  });

  const renderModalBody = () => {
    if (reasonCode) {
      return (
        <Content>
          <EnrollmentReasonMessageV2 reasonCode={reasonCode} s12nId={s12nId || s12n?.id} />
        </Content>
      );
    } else {
      let choiceTypes = getChoiceTypes();
      const canPurchaseCourseWithFullDiscount = canPurchaseSingleCourseAndHasFullDiscount({
        promotionDetails,
        promotionEligibilities,
        enrollmentAvailableChoices,
      });

      // Show only purchase course option for courses with 100% promotion. The discount is applied at checkout.
      if (canPurchaseCourseWithFullDiscount) {
        choiceTypes = [PURCHASE_SINGLE_COURSE];
      }

      let modalBodyMessage = null;

      if (choiceTypes.includes(PURCHASE_SINGLE_COURSE)) {
        modalBodyMessage = (
          <div>
            {!canPurchaseCourseWithFullDiscount && (
              <div css={styles.promoMessage}>
                <PromotionApplicableCheckoutMessage course={course} />
              </div>
            )}
            <Typography2 variant="bodyPrimary" component="div">
              {bodyMessage}
            </Typography2>
          </div>
        );
      } else if (choiceTypes.length === 1 && choiceTypes.includes(ENROLL_COURSE)) {
        modalBodyMessage = (
          <div>
            <Typography2 variant="subtitleMedium" component="div">
              {_t(`This is a full course with no certificate`)}
            </Typography2>
            <Typography2 variant="bodySecondary" component="div">
              {_t(`You will still have access to all course materials for this course.`)}
            </Typography2>
          </div>
        );
      }

      return (
        <>
          <Content>{modalBodyMessage}</Content>
          <Actions>
            {canPurchaseCourseWithFullDiscount ? (
              <TrackedButton
                variant="primary"
                onClick={checkoutAndGoToCourseWithFullDiscount}
                loading={didClickContinue}
                disabled={didClickContinue || didClickPreview}
                trackingName="course_checkout_with_full_discount_and_go_to_course_button"
                data-testid="course_checkout_with_full_discount_and_go_to_course_button"
              >
                {_t('Continue')}
              </TrackedButton>
            ) : (
              <TrackedButton
                variant="primary"
                onClick={onClickContinue}
                loading={didClickContinue}
                disabled={didClickContinue || didClickPreview}
                trackingName="course_enroll_modal_continue_button"
                data-testid="course_enroll_modal_continue_button"
              >
                {primaryCta}
              </TrackedButton>
            )}
            {choiceTypes.length > 1 &&
              [AUDIT_COURSE, ENROLL_COURSE].some((choiceType) => choiceTypes.includes(choiceType)) && (
                <div css={styles.auditCtaContainer}>
                  <Divider />
                  <div css={styles.auditCta}>
                    {isFreemiumModelEnabled(course.id) ? (
                      <>{secondaryCta}</>
                    ) : (
                      <FormattedMessage
                        message={_t('{audit} this course')}
                        audit={
                          // eslint-disable-next-line jsx-a11y/anchor-is-valid
                          <Link
                            component="button"
                            onClick={onClickAuditCourse}
                            disabled={didClickContinue || didClickPreview}
                          >
                            {_t('Audit')}
                          </Link>
                        }
                      />
                    )}
                  </div>
                </div>
              )}
          </Actions>
        </>
      );
    }
  };

  const renderModalContent = () => {
    return (
      <>
        <HeadingGroup>{headerMessage}</HeadingGroup>
        {renderModalBody()}
      </>
    );
  };

  switch (activeModal) {
    case MODAL_TYPES.PRE_PURCHASE_UPSELL:
      return (
        <CourseraPlusPrePurchaseUpsellModal
          onDeclinePrePurchaseUpsell={handlePrePurchaseUpsellClose}
          onError={handleError}
          courseId={course.id}
        />
      );
    case MODAL_TYPES.ERROR:
      return <EnrollErrorModal error={error} onClose={onClose} />;

    case MODAL_TYPES.ENROLL:
      if (!getChoiceTypes().length) {
        return null;
      }
      return (
        <Dialog open onClose={handleModalClose} ref={visibilityTrackingRef}>
          <TrackedDiv withVisibilityTracking={true} trackingName="course_enroll_modal" trackingData={{ id: course.id }}>
            {renderModalContent()}
          </TrackedDiv>
        </Dialog>
      );

    default:
      return null;
  }
};

export default StandaloneCourseEnrollModalV3;
